import React, { useCallback, useEffect, useState } from "react";
import { useCSVReader } from "react-papaparse";

import { checkRegionsUploaded, processRawCropSequestrationData, processRegionData } from "services";

import { useCancelAsyncOnUnmount } from "utils";

import { AdminSection, Anchor, Button, Icon, Message as message } from "components";

const BATCH_SIZE = 5;
const ITEMS_PER_BATCH = 1000;

const chunkArray = (array, size) => {
  const result = [];
  for (let i = 0; i < array.length; i += size) {
    result.push(array.slice(i, i + size));
  }
  return result;
};

const processCSVData = (csvData, keyIndex) => {
  const uniqueKeys = [...new Set(csvData.map((entry) => entry[keyIndex]).filter((key) => key !== undefined))];
  let processedData = [];

  for (const key of uniqueKeys) {
    const entries = csvData.filter((entry) => entry[keyIndex] === key);
    const chunkedEntries = chunkArray(entries, ITEMS_PER_BATCH);
    processedData = [...processedData, ...chunkedEntries];
  }

  return processedData;
};

const processInBatches = async (items, processFunction, signal) => {
  const batches = [];
  for (let i = 0; i < items.length; i += BATCH_SIZE) {
    batches.push(items.slice(i, i + BATCH_SIZE));
  }
  for (const batch of batches) {
    // eslint-disable-next-line no-await-in-loop
    await Promise.all(
      batch.map((item) =>
        processFunction({
          data: item,
          signal,
        })
      )
    );
  }
};

export const CropSequestrationUpload = () => {
  const [regionDataUploaded, setRegionDataUploaded] = useState(false);
  const [loading, setLoading] = useState(false);

  const signal = useCancelAsyncOnUnmount()();
  const { CSVReader } = useCSVReader();

  const uploadCropData = useCallback(
    async (csv) => {
      setLoading(true);
      const csvData = csv.data.slice(1);
      const regionEntriesByUniqueRegionId = processCSVData(csvData, 5);

      await processInBatches(regionEntriesByUniqueRegionId, processRawCropSequestrationData, signal);

      message({ content: "The gremlins have successfully eaten your data", icon: "gremlin" });
      setLoading(false);
    },
    [signal]
  );

  const checkRegionDataUploaded = useCallback(() => {
    checkRegionsUploaded({ signal }).then((res) => setRegionDataUploaded(res.result));
  }, [signal]);

  const uploadRegionData = useCallback(
    async (csv) => {
      setLoading(true);
      const csvData = csv.data.slice(1);
      const stateEntriesByStateCode = processCSVData(csvData, 4);

      await processInBatches(stateEntriesByStateCode, processRegionData, signal);

      message({ content: "The gremlins have successfully eaten your data", icon: "gremlin" });
      setLoading(false);
      setRegionDataUploaded(true);
    },
    [signal]
  );

  useEffect(() => {
    checkRegionDataUploaded();
  }, [checkRegionDataUploaded, regionDataUploaded]);

  return (
    <>
      <h2 className="margin-bottom-200">Regional sequestration document uploads</h2>
      <p className="paragraph-m-bold">
        <Anchor href="https://drive.google.com/drive/folders/1oCt-Y7dAcIPNbvbbla402qqGTj8G8bS9" target="_blank">
          CSV files to be uploaded can be found on our G drive
        </Anchor>
      </p>
      <AdminSection
        headline="Regional data"
        description="Upload data detailing different crop sequestration regions csv here. Once the data is added, it cannot be re-uploaded."
        extra={
          <CSVReader
            onUploadAccepted={(v) => uploadRegionData(v)}
            noDrag
            style={{
              dropArea: {
                border: 0,
                padding: 0,
                margin: 0,
                width: "auto",
                height: "auto",
                display: "inline-block",
              },
              progressBar: {
                display: "none !important",
                opacity: 0,
              },
            }}
          >
            {({ getRootProps }) => (
              <Button disabled={regionDataUploaded || loading} size="small" type="secondary" {...getRootProps()}>
                {regionDataUploaded ? (
                  <>
                    <Icon iconName="tabler/ban" className="icon inline margin-right-25" size="s" />
                    data already uploaded
                  </>
                ) : (
                  <>
                    <Icon iconName="tabler/upload" className="icon inline margin-right-25" size="s" />
                    upload data
                  </>
                )}
              </Button>
            )}
          </CSVReader>
        }
      />
      <AdminSection
        headline="Regional sequestration crop data upload"
        description="Upload regional crop sequestration data csv here."
        extra={
          <CSVReader
            onUploadAccepted={(v) => uploadCropData(v)}
            noDrag
            style={{
              dropArea: {
                border: 0,
                padding: 0,
                margin: 0,
                width: "auto",
                height: "auto",
                display: "inline-block",
              },
              progressBar: {
                display: "none !important",
                opacity: 0,
              },
            }}
          >
            {({ getRootProps }) => (
              <Button loading={loading} type="secondary" disabled={loading || !regionDataUploaded} size="small" {...getRootProps()}>
                {!regionDataUploaded ? (
                  <>
                    <Icon iconName="tabler/ban" className="icon inline margin-right-25" size="s" />
                    region data not uploaded
                  </>
                ) : (
                  <>
                    <Icon iconName="tabler/upload" className="icon inline margin-right-25" size="s" />
                    upload data
                  </>
                )}
              </Button>
            )}
          </CSVReader>
        }
      />
    </>
  );
};
